/**
 * Static guide & documentation content for DocumentationPage and ResearchGuidesPage.
 * These are marketing/design constants — no backend needed.
 * Keyed by slug for lookup from route params.
 */

// ─── Documentation: Platform Guides ─────────────────────────────────────────

const DOC_PLATFORM_GUIDES = {
  'paper-search-discovery': {
    title: 'Paper Search & Discovery',
    description: 'Master AI-powered search, filters, saved papers, citations, and personalized recommendations.',
    icon: 'Search',
    tag: 'Core',
    tagColor: 'green',
    readTime: '10 min read',
    difficulty: 'Beginner',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        paragraphs: [
          'ResearchPro gives you access to over 200 million research papers from every major publisher, preprint server, and open-access repository. Our search engine combines traditional keyword matching with AI-powered semantic understanding, so you find relevant work even when exact terms differ.',
          'Whether you are starting a literature review or tracking the latest publications in your niche, the Paper Search module is your primary discovery tool.',
        ],
      },
      {
        id: 'semantic-search',
        title: 'Using Semantic Search & Filters',
        paragraphs: [
          'Type a research question in natural language — e.g., "methods for detecting gravitational waves" — and the engine returns papers ranked by conceptual relevance, not just keyword frequency. Combine this with filters for year, field, journal, open-access status, and citation count to narrow results fast.',
          'Use the Advanced Search panel to apply Boolean operators (AND, OR, NOT), search within specific fields (title, abstract, authors), and sort by recency or citation impact.',
        ],
      },
      {
        id: 'saving-papers',
        title: 'Saving Papers & Building Collections',
        paragraphs: [
          'Click the bookmark icon on any search result to save it to your personal library. Organize saved papers into named collections — for example, "Thesis Chapter 2" or "Grant Proposal Refs." Collections sync across devices and can be shared with collaborators in a Research Lab.',
          'You can export any collection as BibTeX, RIS, or APA-formatted references with one click.',
        ],
      },
      {
        id: 'citations-recommendations',
        title: 'Citations & Recommendations',
        paragraphs: [
          'Every paper page shows its citation graph — who cited it and what it cites — letting you trace ideas forward and backward through the literature. Use the "Related Papers" sidebar for AI-generated recommendations based on the paper\'s content and your reading history.',
          'Enable personalized recommendations from your profile settings to receive a weekly digest of newly published papers matching your research interests.',
        ],
      },
    ],
    keyTakeaways: [
      'Use natural language queries for best semantic search results.',
      'Combine filters (year, field, journal) to narrow large result sets.',
      'Organize saved papers into collections and share them with your lab.',
      'Explore citation graphs to trace ideas through the literature.',
    ],
    relatedSlugs: ['research-labs', 'mastering-academic-search'],
  },

  'research-labs': {
    title: 'Research Labs',
    description: 'Create labs, manage members, use chat, tasks board, file storage, shared library, and calendar.',
    icon: 'Beaker',
    tag: 'Popular',
    tagColor: 'blue',
    readTime: '12 min read',
    difficulty: 'Beginner',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'what-are-labs',
        title: 'What Are Research Labs?',
        paragraphs: [
          'A Research Lab is your team\'s all-in-one workspace. It bundles real-time chat, a Kanban task board, shared file storage, a collaborative paper library, and a group calendar into a single space that every member can access instantly.',
          'Labs replace the fragmented workflow of switching between Slack, Trello, Google Drive, and email. Everything your team needs lives in one place.',
        ],
      },
      {
        id: 'creating-lab',
        title: 'Creating & Configuring a Lab',
        paragraphs: [
          'Click "Create Lab" from your dashboard, give it a name (e.g., "NLP Research 2026"), add a description, and choose a visibility setting — private (invite-only) or institution-visible. You can also upload a cover image and set a color theme.',
          'After creation, configure channels within the lab for different topics (e.g., #general, #paper-discussion, #experiments). Each channel has its own message thread, keeping conversations organized.',
        ],
      },
      {
        id: 'collaboration-tools',
        title: 'Collaboration Tools: Chat, Tasks & Files',
        paragraphs: [
          'The built-in chat supports rich text, code blocks, LaTeX equations, file attachments, reactions, and threaded replies. Pin important messages for easy reference. Mention @members to notify them directly.',
          'The task board uses a Kanban layout with customizable columns (To Do, In Progress, Review, Done). Assign tasks to members, set due dates, and attach relevant files or papers. The file storage area supports drag-and-drop uploads with version history.',
        ],
      },
      {
        id: 'library-calendar',
        title: 'Shared Library & Calendar',
        paragraphs: [
          'The shared library lets your entire team save, annotate, and discuss papers together. Any member can add papers from search results, and annotations are visible to the whole lab.',
          'The calendar integrates with the task board — deadlines appear automatically. Schedule meetings, set milestone dates, and sync with Google Calendar or Outlook for external visibility.',
        ],
      },
    ],
    keyTakeaways: [
      'Labs combine chat, tasks, files, library, and calendar in one workspace.',
      'Create topic-specific channels to keep conversations organized.',
      'Use the Kanban board for task management with due dates and assignments.',
      'Shared library lets the whole team annotate and discuss papers together.',
    ],
    relatedSlugs: ['paper-search-discovery', 'communities', 'first-research-lab'],
  },

  'communities': {
    title: 'Communities',
    description: 'Join communities, create threads, participate in discussions, moderate content.',
    icon: 'Users',
    tag: 'Essential',
    tagColor: 'purple',
    readTime: '8 min read',
    difficulty: 'Beginner',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'overview',
        title: 'Discovering & Joining Communities',
        paragraphs: [
          'Communities are public or institution-scoped discussion spaces organized around research topics, fields, or interests. Browse the community directory or search by keyword to find groups like "Machine Learning Papers," "Climate Science Discussion," or "Open-Source Research Tools."',
          'Joining is instant — click "Join" and you are in. Some communities require moderator approval for quality control.',
        ],
      },
      {
        id: 'threads',
        title: 'Creating Threads & Discussions',
        paragraphs: [
          'Start a new thread by posting a question, sharing a paper, or starting a discussion. Threads support rich text, embedded paper references, images, code blocks, and polls. Other members can reply, upvote, and bookmark threads.',
          'Use tags to categorize your thread (e.g., "question," "paper-share," "job-posting") so others can filter and find relevant content easily.',
        ],
      },
      {
        id: 'moderation',
        title: 'Moderation & Guidelines',
        paragraphs: [
          'Community creators can set custom guidelines, appoint moderators, and configure auto-moderation rules. Moderators can pin threads, remove off-topic content, and manage member roles.',
          'All communities follow the platform\'s Code of Conduct. Members can report content that violates guidelines, and moderators are notified immediately.',
        ],
      },
      {
        id: 'engagement',
        title: 'Community Engagement Tips',
        paragraphs: [
          'The most active communities share papers regularly, ask and answer questions, and run weekly discussion threads. Consider hosting AMA (Ask Me Anything) sessions or paper reading groups to boost engagement.',
          'Enable email notifications for threads you follow to stay updated without constantly checking the platform.',
        ],
      },
    ],
    keyTakeaways: [
      'Search the community directory to find groups matching your research interests.',
      'Use tags when creating threads to improve discoverability.',
      'Moderators can customize guidelines and auto-moderation rules.',
      'Active communities run regular discussions and paper reading groups.',
    ],
    relatedSlugs: ['research-feed', 'research-labs'],
  },

  'research-feed': {
    title: 'Research Feed',
    description: 'Publish posts, share research, create polls, questions, milestones, and engage with peers.',
    icon: 'FileText',
    tag: null,
    tagColor: null,
    readTime: '7 min read',
    difficulty: 'Beginner',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'publishing',
        title: 'Publishing to Your Feed',
        paragraphs: [
          'The Research Feed is your professional timeline. Share updates about your work — publish posts with text, images, paper references, and links. Your followers and community members see your posts in their feed.',
          'Create different post types: standard updates, paper shares (with automatic metadata extraction), polls for community input, questions for expert advice, and milestones to celebrate achievements.',
        ],
      },
      {
        id: 'post-types',
        title: 'Post Types & Interactions',
        paragraphs: [
          'Each post type has a unique format. Paper shares display the title, authors, and abstract inline. Polls show live vote counts. Questions are highlighted and can be marked "answered." Milestones display achievement badges.',
          'Engage with other researchers by liking, commenting, sharing, and bookmarking posts. Comments support threaded replies, mentions, and rich text.',
        ],
      },
      {
        id: 'network',
        title: 'Building Your Research Network',
        paragraphs: [
          'Follow researchers whose work interests you to see their posts in your feed. The "Discover" tab surfaces trending posts and researchers in your field. Consistent posting and engagement builds your visibility in the research community.',
          'Your feed activity contributes to your professional profile — a strong feed presence helps attract collaborators and visibility for your work.',
        ],
      },
    ],
    keyTakeaways: [
      'Use specific post types (paper share, poll, question) for better engagement.',
      'Follow researchers in your field to build a relevant feed.',
      'Comment and engage regularly to increase your visibility.',
    ],
    relatedSlugs: ['communities', 'messaging-e2ee'],
  },

  'messaging-e2ee': {
    title: 'Messaging & E2EE',
    description: 'Direct messaging with end-to-end encryption, file sharing, reactions, and jump-to-message.',
    icon: 'MessageSquare',
    tag: 'Secure',
    tagColor: 'green',
    readTime: '8 min read',
    difficulty: 'Intermediate',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'basics',
        title: 'Direct Messaging Basics',
        paragraphs: [
          'Send direct messages to any user on the platform. Conversations support rich text, file attachments (up to 25 MB), emoji reactions, and threaded replies. Use the search bar within a conversation to find specific messages instantly.',
          'The "Jump to Message" feature lets you navigate to any message by date or keyword, making it easy to reference past discussions.',
        ],
      },
      {
        id: 'e2ee',
        title: 'End-to-End Encryption Explained',
        paragraphs: [
          'All direct messages are encrypted end-to-end using ECDH P-256 key exchange and AES-256-GCM cipher. This means only you and the recipient can read your messages — not even the platform administrators have access to plaintext content.',
          'Encryption keys are generated on your device and never leave it. When you log in on a new device, you will need to verify your identity to restore encrypted message access.',
        ],
      },
      {
        id: 'file-sharing',
        title: 'File Sharing & Reactions',
        paragraphs: [
          'Share documents, images, datasets, and code files directly in conversations. Files are encrypted in transit and at rest. Preview PDFs and images inline without downloading.',
          'React to messages with emoji to acknowledge or respond quickly without typing. Pin important messages to keep them accessible at the top of the conversation.',
        ],
      },
    ],
    keyTakeaways: [
      'All DMs are encrypted end-to-end with ECDH P-256 + AES-256-GCM.',
      'Use Jump-to-Message to quickly find past conversations.',
      'Files shared in messages are encrypted in transit and at rest.',
    ],
    relatedSlugs: ['encryption-security', 'research-labs'],
  },

  'courses-workshops': {
    title: 'Courses & Workshops',
    description: 'Browse courses, attend workshops, manage enrollments, submit feedback, and track progress.',
    icon: 'GraduationCap',
    tag: 'New',
    tagColor: 'amber',
    readTime: '7 min read',
    difficulty: 'Beginner',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'browsing',
        title: 'Browsing & Enrolling in Courses',
        paragraphs: [
          'The Courses section offers structured learning experiences created by researchers and institutions. Browse by topic, difficulty, or institution. Each course page shows the syllabus, instructor bio, estimated time, and reviews from past participants.',
          'Enroll with one click. Courses appear in your dashboard with progress tracking, and you receive reminders for upcoming deadlines.',
        ],
      },
      {
        id: 'workshops',
        title: 'Attending Workshops',
        paragraphs: [
          'Workshops are live, interactive sessions — think hands-on tutorials, coding labs, or methodology deep-dives. Check the schedule, register in advance, and receive calendar invites with join links.',
          'Workshops include live Q&A, shared whiteboards, and breakout rooms for group exercises. Recordings are available for registered participants who miss the live session.',
        ],
      },
      {
        id: 'progress',
        title: 'Tracking Your Learning Progress',
        paragraphs: [
          'Your learning dashboard shows enrolled courses, completion percentage, upcoming workshops, and earned certificates. Set weekly learning goals, and the platform tracks your streak.',
          'After completing a course or workshop, submit feedback to help instructors improve. Top-rated courses are featured in the browse page.',
        ],
      },
    ],
    keyTakeaways: [
      'Browse courses by topic, difficulty, or institution.',
      'Workshops offer live, interactive learning with recordings available.',
      'Track progress, set learning goals, and earn certificates.',
    ],
    relatedSlugs: ['research-labs', 'communities'],
  },
};

// ─── Documentation: Developer Resources ─────────────────────────────────────

const DOC_DEVELOPER_GUIDES = {
  'api-overview': {
    title: 'API Overview',
    description: 'RESTful architecture, authentication, rate limiting, and response formats.',
    icon: 'Code',
    tag: 'Developer',
    tagColor: 'blue',
    readTime: '8 min read',
    difficulty: 'Intermediate',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'architecture',
        title: 'Architecture & Base URL',
        paragraphs: [
          'The ResearchPro API follows RESTful conventions. All endpoints are served over HTTPS from a versioned base URL. Requests and responses use JSON format with UTF-8 encoding.',
          'The current API version is v1. Include the version in the URL path: https://api.ResearchPro.io/v1/. All timestamps are returned in ISO 8601 format (UTC).',
        ],
      },
      {
        id: 'request-response',
        title: 'Request & Response Formats',
        paragraphs: [
          'Every successful response wraps data in a standard envelope: { "success": true, "data": { ... } }. Error responses include a machine-readable error code and a human-readable message: { "success": false, "error": "INVALID_TOKEN", "message": "..." }.',
          'Paginated endpoints return a pagination object with totalDocs, page, limit, totalPages, and hasNextPage fields.',
        ],
      },
      {
        id: 'rate-limiting',
        title: 'Rate Limiting & Pagination',
        paragraphs: [
          'API requests are rate-limited per API key. Default limits are 100 requests per minute for standard keys and 1,000 for premium keys. Rate limit headers (X-RateLimit-Limit, X-RateLimit-Remaining, X-RateLimit-Reset) are included in every response.',
          'Use cursor-based pagination for large datasets. Pass ?page=N&limit=M to paginated endpoints. Maximum page size is 100 items.',
        ],
      },
    ],
    keyTakeaways: [
      'All endpoints use HTTPS, JSON, and versioned URLs.',
      'Responses follow a standard { success, data } envelope.',
      'Rate limits are per API key — check response headers.',
    ],
    relatedSlugs: ['authentication', 'endpoints-reference'],
  },

  'authentication': {
    title: 'Authentication',
    description: 'JWT-based auth, OAuth 2.0, token refresh, and session management.',
    icon: 'Key',
    tag: 'Developer',
    tagColor: 'blue',
    readTime: '9 min read',
    difficulty: 'Intermediate',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'jwt',
        title: 'JWT-Based Authentication',
        paragraphs: [
          'ResearchPro uses JSON Web Tokens (JWT) for API authentication. After a successful login, the server returns an access token (short-lived, 15 minutes) and a refresh token (long-lived, 7 days). Include the access token in the Authorization header: Bearer <token>.',
          'Tokens are signed with RS256. Verify the signature using the platform\'s public key, available at /.well-known/jwks.json.',
        ],
      },
      {
        id: 'oauth',
        title: 'OAuth 2.0 Integration',
        paragraphs: [
          'For third-party integrations, ResearchPro supports the OAuth 2.0 Authorization Code flow. Register your application in the developer console to obtain a client_id and client_secret. Redirect users to /oauth/authorize with the required scopes.',
          'Supported scopes include read:profile, write:papers, read:rooms, write:messages, and admin:community. Always request the minimum scopes your application needs.',
        ],
      },
      {
        id: 'token-refresh',
        title: 'Token Refresh & Session Management',
        paragraphs: [
          'When the access token expires, send the refresh token to /auth/refresh to obtain a new pair. If the refresh token is also expired, the user must re-authenticate.',
          'Sessions are tracked server-side. Users can view and revoke active sessions from their account settings. Revoking a session invalidates all tokens associated with it.',
        ],
      },
    ],
    keyTakeaways: [
      'Access tokens expire in 15 minutes; use refresh tokens to renew.',
      'OAuth 2.0 Authorization Code flow is supported for third-party apps.',
      'Users can view and revoke sessions from account settings.',
    ],
    relatedSlugs: ['api-overview', 'encryption-security'],
  },

  'endpoints-reference': {
    title: 'Endpoints Reference',
    description: 'Complete endpoint documentation for papers, rooms, communities, feed, and messaging.',
    icon: 'Database',
    tag: 'Developer',
    tagColor: 'blue',
    readTime: '15 min read',
    difficulty: 'Advanced',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'papers',
        title: 'Papers API',
        paragraphs: [
          'GET /papers/search — Search papers with query, filters, and pagination. GET /papers/:id — Retrieve full paper metadata including abstract, authors, citations, and PDF link. POST /papers/:id/save — Save a paper to your library.',
          'GET /papers/:id/citations — Get citing and cited papers. GET /papers/recommendations — Personalized paper recommendations based on reading history and saved papers.',
        ],
      },
      {
        id: 'rooms',
        title: 'Rooms (Labs) API',
        paragraphs: [
          'POST /rooms — Create a new lab. GET /rooms/:id — Get lab details, members, and settings. PUT /rooms/:id — Update lab settings. DELETE /rooms/:id — Archive a lab (requires owner role).',
          'POST /rooms/:id/tasks — Create a task. PUT /rooms/:id/tasks/:taskId — Update task status, assignee, or due date. GET /rooms/:id/files — List uploaded files. POST /rooms/:id/files — Upload a file.',
        ],
      },
      {
        id: 'communities-feed',
        title: 'Communities & Feed APIs',
        paragraphs: [
          'GET /communities — List communities with optional keyword filter. POST /communities — Create a community. POST /communities/:id/threads — Create a thread. GET /communities/:id/threads — List threads with pagination and sort options.',
          'GET /feed — Get personalized feed items. POST /feed — Publish a post (text, paper-share, poll, question, milestone). POST /feed/:id/like — Toggle like on a post. POST /feed/:id/comments — Add a comment.',
        ],
      },
      {
        id: 'messaging',
        title: 'Messaging API',
        paragraphs: [
          'GET /messages/conversations — List conversations. GET /messages/:conversationId — Get messages in a conversation with pagination. POST /messages/:conversationId — Send a message (encrypted payload).',
          'POST /messages/:conversationId/files — Upload an encrypted file attachment. PUT /messages/:messageId/reactions — Add or remove a reaction. WebSocket events handle real-time message delivery (see WebSocket Events guide).',
        ],
      },
    ],
    keyTakeaways: [
      'Papers API supports search, save, citations, and recommendations.',
      'Rooms API covers the full lifecycle: create, manage, tasks, files.',
      'All messaging endpoints handle encrypted payloads.',
    ],
    relatedSlugs: ['api-overview', 'websocket-events'],
  },

  'websocket-events': {
    title: 'WebSocket Events',
    description: 'Real-time socket events for messaging, rooms, communities, and notifications.',
    icon: 'Terminal',
    tag: 'Developer',
    tagColor: 'blue',
    readTime: '10 min read',
    difficulty: 'Advanced',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'connection',
        title: 'Connection & Authentication',
        paragraphs: [
          'Connect to the WebSocket server at wss://api.ResearchPro.io/ws with your JWT access token passed as a query parameter or in the auth handshake. The server acknowledges with a "connected" event containing your user ID and active subscriptions.',
          'If the token expires during a session, the server emits a "token_expired" event. Reconnect with a fresh access token to resume.',
        ],
      },
      {
        id: 'messaging-events',
        title: 'Messaging & Room Events',
        paragraphs: [
          'message:new — Emitted when a new message is received in any conversation you are part of. message:updated — When a message is edited. message:deleted — When a message is removed. typing:start / typing:stop — Typing indicators.',
          'room:message — New message in a lab channel. room:task_updated — Task status change. room:member_joined / room:member_left — Membership changes. room:file_uploaded — New file added to the lab.',
        ],
      },
      {
        id: 'community-notification',
        title: 'Community & Notification Events',
        paragraphs: [
          'community:new_thread — New thread posted. community:reply — Reply to a thread you follow. community:mention — Someone mentioned you. These events carry the thread ID, community ID, and a preview of the content.',
          'notification:new — General notification (likes, follows, task assignments). presence:update — Online/offline status changes for users you follow.',
        ],
      },
    ],
    keyTakeaways: [
      'Authenticate WebSocket connections with your JWT token.',
      'Listen for message:new and room:message for real-time chat.',
      'Handle token_expired events by reconnecting with a fresh token.',
    ],
    relatedSlugs: ['endpoints-reference', 'encryption-security'],
  },

  'encryption-security': {
    title: 'Encryption & Security',
    description: 'End-to-end encryption with ECDH P-256 key exchange and AES-256-GCM cipher.',
    icon: 'Shield',
    tag: 'Developer',
    tagColor: 'blue',
    readTime: '12 min read',
    difficulty: 'Advanced',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'overview',
        title: 'E2EE Implementation Overview',
        paragraphs: [
          'ResearchPro implements end-to-end encryption (E2EE) for all direct messages. The encryption is client-side only — the server never sees plaintext content. This ensures that even in the event of a server breach, message content remains confidential.',
          'The implementation uses the Web Crypto API available in modern browsers, requiring no additional dependencies on the client side.',
        ],
      },
      {
        id: 'key-exchange',
        title: 'ECDH P-256 Key Exchange',
        paragraphs: [
          'Each user generates an ECDH key pair (P-256 curve) on first login. The public key is uploaded to the server; the private key is stored in the browser\'s IndexedDB, encrypted with a key derived from the user\'s password.',
          'When starting a conversation, both parties exchange public keys through the server. Each client derives a shared secret using ECDH, from which a symmetric AES-256-GCM key is generated using HKDF-SHA256.',
        ],
      },
      {
        id: 'aes-gcm',
        title: 'AES-256-GCM Message Encryption',
        paragraphs: [
          'Each message is encrypted with AES-256-GCM using the derived shared key and a unique 96-bit IV (initialization vector). The IV is prepended to the ciphertext and sent alongside the authentication tag, ensuring both confidentiality and integrity.',
          'File attachments follow the same encryption flow. Large files are chunked and each chunk is encrypted independently, allowing streaming decryption on the recipient\'s side.',
        ],
      },
    ],
    keyTakeaways: [
      'E2EE uses ECDH P-256 for key exchange and AES-256-GCM for encryption.',
      'Private keys never leave the user\'s device.',
      'The server only handles ciphertext — plaintext is never transmitted or stored.',
    ],
    relatedSlugs: ['messaging-e2ee', 'authentication'],
  },

  'webhooks-integrations': {
    title: 'Webhooks & Integrations',
    description: 'Event-driven webhooks for community activity, research updates, and task changes.',
    icon: 'Layers',
    tag: 'Developer',
    tagColor: 'blue',
    readTime: '9 min read',
    difficulty: 'Intermediate',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'setup',
        title: 'Setting Up Webhooks',
        paragraphs: [
          'Register webhook endpoints from the Developer Console. Provide a URL, select the events you want to receive, and optionally set a secret for signature verification. The platform sends a test ping to verify your endpoint before activation.',
          'Webhooks are retried up to 3 times with exponential backoff if your endpoint returns a non-2xx status code. Failed deliveries are logged in the Developer Console.',
        ],
      },
      {
        id: 'events',
        title: 'Event Types & Payloads',
        paragraphs: [
          'Available events include: paper.saved, room.task.created, room.task.completed, community.thread.created, community.reply.created, feed.post.created, and user.followed. Each payload includes a timestamp, event type, and the full resource object.',
          'All webhook requests include an X-ResearchPro-Signature header containing an HMAC-SHA256 signature of the request body, computed using your webhook secret. Always verify this signature before processing the payload.',
        ],
      },
      {
        id: 'best-practices',
        title: 'Integration Best Practices',
        paragraphs: [
          'Respond to webhook requests within 10 seconds with a 200 status code. If processing takes longer, acknowledge the webhook immediately and process asynchronously.',
          'Use webhook events to build integrations with Slack, Notion, Zapier, or your own internal tools. For example, post a Slack notification when a new task is created in your lab, or update a Notion database when a community thread is posted.',
        ],
      },
    ],
    keyTakeaways: [
      'Register webhooks from the Developer Console with event selection.',
      'Always verify the HMAC-SHA256 signature before processing.',
      'Process webhook payloads asynchronously for reliability.',
    ],
    relatedSlugs: ['api-overview', 'websocket-events'],
  },
};

// ─── Research Guides ────────────────────────────────────────────────────────

const RESEARCH_GUIDES = {
  'complete-literature-reviews': {
    title: 'The Complete Guide to Literature Reviews',
    description: 'Learn systematic search strategies, evaluation frameworks, and synthesis methods using our 200M+ paper database.',
    icon: 'Compass',
    tag: 'Essential',
    tagColor: 'green',
    readTime: '15 min read',
    difficulty: 'Beginner',
    lastUpdated: 'January 2026',
    sections: [
      {
        id: 'importance',
        title: 'Why Literature Reviews Matter',
        paragraphs: [
          'A literature review is the foundation of every research project. It establishes what is already known about your topic, identifies gaps in the existing body of work, and positions your research within the broader scholarly conversation.',
          'A well-executed review saves you from duplicating existing work, helps you refine your research questions, and demonstrates to reviewers and funders that you have a thorough understanding of the field.',
        ],
      },
      {
        id: 'search-strategy',
        title: 'Systematic Search Strategy',
        paragraphs: [
          'Start by defining your research question clearly. Break it into key concepts and generate synonyms, related terms, and Boolean combinations. Use ResearchPro\'s semantic search to find papers that match the conceptual meaning, not just exact keywords.',
          'Search across multiple databases — ResearchPro aggregates over 200 million papers from PubMed, arXiv, IEEE, Springer, and more. Set up alerts for new publications matching your search criteria to stay current as your review progresses.',
        ],
      },
      {
        id: 'evaluating-sources',
        title: 'Evaluating & Selecting Sources',
        paragraphs: [
          'Apply inclusion and exclusion criteria consistently. Consider factors like publication date, methodology quality, journal reputation, and relevance to your specific questions. Use ResearchPro citation metrics to identify highly-cited (foundational) papers.',
          'Create a screening workflow: first-pass by title and abstract, second-pass by full text. Save promising papers to a dedicated collection and tag them with themes or quality ratings for easy reference later.',
        ],
      },
      {
        id: 'synthesis',
        title: 'Synthesizing Your Findings',
        paragraphs: [
          'Don\'t just summarize papers individually — synthesize across them. Group findings by theme, methodology, or outcome. Look for consensus, contradictions, and evolving perspectives across the literature.',
          'Use concept maps or synthesis matrices to organize your findings visually. ResearchPro\'s shared lab library makes it easy to collaborate on literature reviews with your team, annotating papers and sharing notes in context.',
        ],
      },
      {
        id: 'writing',
        title: 'Writing the Review',
        paragraphs: [
          'Structure your review logically — start with broad context, narrow to your specific topic, and end with identified gaps that your research addresses. Each paragraph should synthesize multiple sources, not just describe one paper at a time.',
          'Use proper citation practices throughout. Export your ResearchPro collection as BibTeX for seamless integration with LaTeX, or as formatted references for Word documents.',
        ],
      },
    ],
    keyTakeaways: [
      'Define your research question before searching to stay focused.',
      'Use semantic search to find conceptually related papers, not just keyword matches.',
      'Apply consistent inclusion/exclusion criteria for systematic screening.',
      'Synthesize across papers by theme, not paper-by-paper summarization.',
      'Export citations directly from ResearchPro to your manuscript.',
    ],
    relatedSlugs: ['mastering-academic-search', 'writing-papers-published'],
  },

  'high-performance-research-teams': {
    title: 'Building High-Performance Research Teams',
    description: 'Best practices for assembling teams, setting goals, managing communications, and leveraging Research Labs.',
    icon: 'Users',
    tag: 'Popular',
    tagColor: 'blue',
    readTime: '12 min read',
    difficulty: 'Intermediate',
    lastUpdated: 'January 2026',
    sections: [
      {
        id: 'assembling',
        title: 'Assembling the Right Team',
        paragraphs: [
          'High-performance research teams combine complementary skills — domain expertise, methodology knowledge, technical capabilities, and writing proficiency. Look for collaborators who bring different perspectives and strengths.',
          'Use ResearchPro\'s researcher profiles to evaluate potential collaborators based on their publications, interests, and activity. Reach out through direct messaging to discuss collaboration opportunities before committing.',
        ],
      },
      {
        id: 'goals',
        title: 'Setting Clear Goals & Roles',
        paragraphs: [
          'Define the project\'s objectives, timeline, and deliverables upfront. Use a Research Lab to document these in pinned messages or the shared file space so every member has access.',
          'Assign clear roles: lead author, methodology expert, data analyst, writing editor. Use the task board to break the project into milestones with owners and deadlines. This prevents duplication of effort and ensures accountability.',
        ],
      },
      {
        id: 'communication',
        title: 'Communication & Collaboration Tools',
        paragraphs: [
          'Establish communication norms early — decide on a channel for quick questions, a time for weekly sync meetings (added to the lab calendar), and a protocol for document reviews.',
          'ResearchPro Labs centralize everything: chat for discussions, tasks for tracking, files for documents, and library for shared papers. Teams that avoid scattering communication across multiple platforms are consistently more productive.',
        ],
      },
      {
        id: 'conflict',
        title: 'Managing Conflicts & Deadlines',
        paragraphs: [
          'Disagreements are natural in research teams. Address them early by discussing methodology choices, authorship order, and data ownership before they become contentious. Document decisions in the lab.',
          'When deadlines slip (and they will), re-prioritize tasks on the Kanban board rather than trying to do everything at once. Break large tasks into smaller, achievable chunks to maintain momentum.',
        ],
      },
    ],
    keyTakeaways: [
      'Build teams with complementary skills, not identical expertise.',
      'Document goals, roles, and authorship agreements early in the project.',
      'Use a single platform (like a Research Lab) for all team communication.',
      'Address conflicts and re-prioritize proactively instead of reactively.',
    ],
    relatedSlugs: ['first-research-lab', 'cross-institutional-collaboration'],
  },

  'writing-papers-published': {
    title: 'Writing Papers That Get Published',
    description: 'From structuring manuscripts to navigating peer review. Includes templates and expert tips.',
    icon: 'PenTool',
    tag: 'Featured',
    tagColor: 'purple',
    readTime: '20 min read',
    difficulty: 'Advanced',
    lastUpdated: 'December 2025',
    sections: [
      {
        id: 'structure',
        title: 'Manuscript Structure & Formatting',
        paragraphs: [
          'Most journals follow the IMRaD format: Introduction, Methods, Results, and Discussion. Each section has a clear purpose: the Introduction frames the problem; Methods describes how you studied it; Results presents what you found; Discussion interprets the findings.',
          'Before writing, check your target journal\'s author guidelines for formatting requirements, word limits, and reference style. Tailoring your manuscript to the journal increases acceptance probability.',
        ],
      },
      {
        id: 'abstract',
        title: 'Writing Compelling Abstracts',
        paragraphs: [
          'The abstract is the most-read part of your paper. Write it last, after the manuscript is complete. Include the research question, methodology, key findings, and main conclusion — all within the word limit (typically 150–300 words).',
          'Use concrete numbers where possible ("We analyzed 10,000 patient records" not "We analyzed a large dataset"). A strong abstract determines whether reviewers and readers engage with the full paper.',
        ],
      },
      {
        id: 'results',
        title: 'Results & Discussion Sections',
        paragraphs: [
          'Present results objectively without interpretation — save that for the Discussion. Use tables and figures to convey complex data clearly. Every figure should have a descriptive caption that allows it to be understood without reading the main text.',
          'In the Discussion, interpret your results in the context of existing literature. Acknowledge limitations honestly — reviewers will find them regardless, and proactive acknowledgment builds credibility.',
        ],
      },
      {
        id: 'peer-review',
        title: 'Navigating Peer Review',
        paragraphs: [
          'Peer review is not an adversarial process — reviewers want to help improve your work. Address every comment in your response letter, even if you disagree. Organize your response point-by-point with clear references to changes in the manuscript.',
          'If a reviewer misunderstood something, it usually indicates unclear writing. Rather than simply explaining what you meant, revise the relevant section to prevent future misunderstandings.',
        ],
      },
      {
        id: 'revision',
        title: 'Revision & Resubmission',
        paragraphs: [
          'A "revise and resubmit" decision is not a rejection — it\'s an invitation to improve. Most published papers go through at least one round of revision. Prioritize addressing critical concerns while noting minor comments you have also fixed.',
          'Track changes in your manuscript to make it easy for reviewers to see modifications. If your paper is rejected, read the reviews carefully, improve the manuscript, and resubmit to another suitable journal.',
        ],
      },
    ],
    keyTakeaways: [
      'Follow IMRaD structure and check target journal guidelines before writing.',
      'Write the abstract last, with concrete numbers and clear conclusions.',
      'Acknowledge limitations proactively in the Discussion section.',
      'Address every peer review comment, even if you disagree.',
      '"Revise and resubmit" is an opportunity, not a rejection.',
    ],
    relatedSlugs: ['complete-literature-reviews', 'maximizing-research-impact'],
  },

  'mastering-academic-search': {
    title: 'Mastering Academic Search',
    description: 'Advanced search operators, semantic search, citation tracking, and building reading lists.',
    icon: 'Search',
    tag: null,
    tagColor: null,
    readTime: '8 min read',
    difficulty: 'Beginner',
    lastUpdated: 'January 2026',
    sections: [
      {
        id: 'operators',
        title: 'Search Operators & Techniques',
        paragraphs: [
          'Use Boolean operators to refine your searches: AND narrows results (both terms must appear), OR broadens them (either term), and NOT excludes terms. Enclose phrases in quotes for exact matching — "deep learning" finds the exact phrase, not pages mentioning "deep" and "learning" separately.',
          'Use field-specific search to target titles (title:"climate change"), authors (author:"Smith"), or abstracts. Combine operators for precision: author:"Lee" AND title:"neural network" AND year:2024-2026.',
        ],
      },
      {
        id: 'semantic',
        title: 'Semantic Search vs. Keyword Search',
        paragraphs: [
          'Keyword search finds papers containing your exact terms. Semantic search understands the meaning behind your query — searching "how does caffeine affect memory" will find papers about "cognitive enhancement through methylxanthine compounds" even though none of your words appear.',
          'Start with semantic search to explore broadly, then switch to keyword search with operators to find specific papers. This two-phase approach gives you both breadth and precision.',
        ],
      },
      {
        id: 'citation-tracking',
        title: 'Citation Tracking & Alerts',
        paragraphs: [
          'Track citations in both directions: "Cited by" shows who built on a paper\'s work; "References" shows what the paper was built on. Following citation chains forward is one of the best ways to find state-of-the-art work.',
          'Set up email alerts for saved searches and specific papers. You will be notified when new papers match your search criteria or when a saved paper receives a new citation.',
        ],
      },
      {
        id: 'reading-lists',
        title: 'Building & Organizing Reading Lists',
        paragraphs: [
          'Create themed collections for different projects or chapters. Add notes to each saved paper summarizing key findings and relevance to your work. Sort collections by date added, publication year, or your own priority ranking.',
          'Share collections with lab members using Research Labs. Collaborative collections let multiple researchers add papers, reducing duplicate discovery effort.',
        ],
      },
    ],
    keyTakeaways: [
      'Use Boolean operators and field-specific search for precision.',
      'Start with semantic search for exploration, then use keywords for specifics.',
      'Track citation chains forward to find the latest work.',
      'Organize papers into themed collections with notes for easy reference.',
    ],
    relatedSlugs: ['paper-search-discovery', 'complete-literature-reviews'],
  },

  'first-research-lab': {
    title: 'Setting Up Your First Research Lab',
    description: 'Step-by-step walkthrough of creating a lab, inviting collaborators, and configuring tools.',
    icon: 'Beaker',
    tag: null,
    tagColor: null,
    readTime: '10 min read',
    difficulty: 'Beginner',
    lastUpdated: 'January 2026',
    sections: [
      {
        id: 'planning',
        title: 'Planning Your Lab Structure',
        paragraphs: [
          'Before creating a lab, decide on its scope and purpose. Is it for a specific project ("ICML 2026 Paper") or an ongoing research group ("Computational Biology Lab")? This determines how you name it, structure channels, and set permissions.',
          'Think about who will join, what tools they need, and how long the lab will be active. Short-term project labs might have a simpler structure; ongoing group labs benefit from more channels and organized file storage.',
        ],
      },
      {
        id: 'inviting',
        title: 'Inviting Collaborators',
        paragraphs: [
          'From the lab\'s member settings, invite collaborators by email or username. Set roles: Owner (full control), Admin (manage members and settings), or Member (access all tools). You can adjust roles later as the project evolves.',
          'For large groups, share an invite link that new members can use to join. Enable approval mode if you want to vet new members before they gain access.',
        ],
      },
      {
        id: 'configuring',
        title: 'Configuring Tools & Channels',
        paragraphs: [
          'Create channels for different topics: #general for announcements, #paper-discussion for literature, #experiments for methodology, and #writing for manuscript drafts. This keeps conversations searchable and organized.',
          'Set up the task board with columns matching your workflow — for example: Backlog → This Week → In Progress → Review → Done. Upload a project timeline document to files, and add key deadlines to the shared calendar.',
        ],
      },
      {
        id: 'kickoff',
        title: 'First Project Kickoff',
        paragraphs: [
          'Post a welcome message in #general outlining the project goals, timeline, and each member\'s role. Pin this message so newcomers can reference it anytime. Create initial tasks on the board and assign them to kick things off.',
          'Schedule a kickoff meeting using the lab calendar. During the meeting, walk through the lab tools, confirm everyone can access files and channels, and agree on communication norms.',
        ],
      },
    ],
    keyTakeaways: [
      'Define the lab\'s scope before creating it — project-specific or ongoing group.',
      'Set roles (Owner, Admin, Member) to control access appropriately.',
      'Create topic-specific channels and a structured task board from day one.',
      'Post a pinned welcome message with goals, timeline, and roles.',
    ],
    relatedSlugs: ['research-labs', 'high-performance-research-teams'],
  },

  'research-methodology-framework': {
    title: 'Research Methodology Framework',
    description: 'Choosing the right methodology — qualitative, quantitative, and mixed-methods compared.',
    icon: 'Target',
    tag: null,
    tagColor: null,
    readTime: '18 min read',
    difficulty: 'Intermediate',
    lastUpdated: 'December 2025',
    sections: [
      {
        id: 'qualitative',
        title: 'Qualitative Methods Overview',
        paragraphs: [
          'Qualitative research explores subjective experiences, meanings, and social phenomena through methods like interviews, focus groups, ethnography, and content analysis. It answers "how" and "why" questions.',
          'Common approaches include grounded theory (building theory from data), phenomenology (understanding lived experiences), and case studies (in-depth analysis of specific instances). Choose the approach that aligns with your research questions.',
        ],
      },
      {
        id: 'quantitative',
        title: 'Quantitative Methods Overview',
        paragraphs: [
          'Quantitative research uses numerical data to test hypotheses and identify patterns. Methods include surveys, experiments, quasi-experiments, and statistical analysis of existing datasets. It answers "what," "how many," and "to what extent."',
          'Key considerations include sample size (use power analysis to determine the minimum), variable operationalization (how you measure abstract concepts), and statistical test selection (t-tests, ANOVA, regression, etc.).',
        ],
      },
      {
        id: 'mixed',
        title: 'Mixed-Methods Approach',
        paragraphs: [
          'Mixed-methods research combines qualitative and quantitative approaches to leverage the strengths of both. For example, start with interviews to identify themes, then use a survey to test how widespread those themes are across a population.',
          'Common designs include sequential explanatory (quantitative first, qualitative to explain), sequential exploratory (qualitative first, quantitative to test), and concurrent (both simultaneously). Document your rationale for the chosen design.',
        ],
      },
      {
        id: 'choosing',
        title: 'Choosing the Right Methodology',
        paragraphs: [
          'Your methodology should be driven by your research question, not personal preference. Ask: Am I exploring a new phenomenon (qualitative)? Testing a hypothesis (quantitative)? Or need both depth and breadth (mixed-methods)?',
          'Consider practical constraints too: available data, time, budget, and your team\'s methodological expertise. A well-executed simple design is better than a poorly-executed complex one.',
        ],
      },
    ],
    keyTakeaways: [
      'Let your research question drive methodology choice, not preference.',
      'Qualitative answers "how/why," quantitative answers "how much/how many."',
      'Mixed-methods combines both for depth and breadth.',
      'Consider practical constraints: data, time, budget, and team skills.',
    ],
    relatedSlugs: ['data-analysis-best-practices', 'writing-papers-published'],
  },

  'data-analysis-best-practices': {
    title: 'Data Analysis Best Practices',
    description: 'Statistical methods, visualization techniques, reproducibility checklists, and tools.',
    icon: 'BarChart3',
    tag: null,
    tagColor: null,
    readTime: '14 min read',
    difficulty: 'Advanced',
    lastUpdated: 'December 2025',
    sections: [
      {
        id: 'stats',
        title: 'Statistical Methods Overview',
        paragraphs: [
          'Choose statistical tests based on your data type and research design. For comparing groups: t-tests (two groups), ANOVA (multiple groups). For relationships: correlation (linear association), regression (prediction). For categorical data: chi-square tests.',
          'Always check assumptions before running tests — normality, homogeneity of variance, independence of observations. Report effect sizes alongside p-values to convey practical significance, not just statistical significance.',
        ],
      },
      {
        id: 'visualization',
        title: 'Data Visualization Techniques',
        paragraphs: [
          'Good visualizations make complex data accessible. Use bar charts for group comparisons, scatter plots for relationships, line charts for trends over time, and heatmaps for correlation matrices. Avoid 3D charts and pie charts with many slices.',
          'Every figure should have clear axis labels, descriptive captions, and appropriate scales. Use colorblind-friendly palettes. In publications, ensure figures are readable at the journal\'s print resolution (typically 300 DPI).',
        ],
      },
      {
        id: 'reproducibility',
        title: 'Reproducibility & Documentation',
        paragraphs: [
          'Document every step of your analysis: data cleaning decisions, variable transformations, parameter choices, and software versions. Use scripts (R, Python) rather than point-and-click tools so your analysis can be re-executed exactly.',
          'Share your data and code in a public repository (e.g., GitHub, Zenodo) when publishing. A reproducibility checklist: raw data preserved → cleaning steps documented → analysis scripts versioned → results verified by a co-author.',
        ],
      },
      {
        id: 'tools',
        title: 'Tools & Software Recommendations',
        paragraphs: [
          'For statistical analysis: R (comprehensive, free, package ecosystem), Python with Pandas/SciPy/Statsmodels (versatile, integrates with ML), SPSS (GUI-based, common in social sciences), Stata (econometrics, panel data).',
          'For visualization: Matplotlib/Seaborn (Python), ggplot2 (R), Plotly (interactive), and Tableau (no-code dashboards). For version control: Git with GitHub or GitLab to track code changes.',
        ],
      },
    ],
    keyTakeaways: [
      'Match statistical tests to your data type and research design.',
      'Report effect sizes alongside p-values for meaningful results.',
      'Document every analysis step and use scripts for reproducibility.',
      'Share data and code publicly when publishing.',
    ],
    relatedSlugs: ['research-methodology-framework', 'research-ethics-integrity'],
  },

  'research-ethics-integrity': {
    title: 'Research Ethics & Integrity',
    description: 'Authorship guidelines, data management, IRB protocols, and open science principles.',
    icon: 'Shield',
    tag: null,
    tagColor: null,
    readTime: '11 min read',
    difficulty: 'Beginner',
    lastUpdated: 'January 2026',
    sections: [
      {
        id: 'authorship',
        title: 'Authorship & Attribution',
        paragraphs: [
          'Follow the ICMJE criteria for authorship: substantial contributions to conception/design OR data acquisition/analysis, drafting or critically revising the manuscript, final approval, and accountability. Gift authorship (adding names for prestige) and ghost authorship (omitting contributors) are both unethical.',
          'Discuss authorship order early in the project. In many fields, the first author did the most work, and the last author is the senior/supervising researcher. Document these agreements in your Research Lab.',
        ],
      },
      {
        id: 'data-management',
        title: 'Data Management & Sharing',
        paragraphs: [
          'Create a data management plan at the start of every project. Specify how data will be collected, stored, backed up, anonymized (if human subjects), and eventually shared or archived.',
          'Use secure, versioned storage for raw data — never modify raw files directly. Create analysis-ready copies with documented transformations. Follow FAIR principles: Findable, Accessible, Interoperable, Reusable.',
        ],
      },
      {
        id: 'irb',
        title: 'IRB & Regulatory Compliance',
        paragraphs: [
          'Research involving human subjects requires Institutional Review Board (IRB) or Ethics Committee approval before data collection begins. Prepare your application with a clear description of recruitment, consent procedures, risks, benefits, and data protection measures.',
          'Keep consent forms, IRB approval letters, and protocol amendments organized. If your research involves vulnerable populations, additional safeguards may be required. When in doubt, consult your institution\'s research compliance office.',
        ],
      },
      {
        id: 'open-science',
        title: 'Open Science Principles',
        paragraphs: [
          'Open science promotes transparency, accessibility, and reproducibility. Core practices include preregistering studies (publishing your hypotheses and methods before data collection), sharing data and code, and publishing in open-access journals or preprint servers.',
          'ResearchPro supports open science through public research profiles, paper sharing in communities, and collaborative labs where methods and data can be discussed openly.',
        ],
      },
    ],
    keyTakeaways: [
      'Agree on authorship criteria and order at the start of every project.',
      'Create a data management plan before collecting any data.',
      'Obtain IRB approval before any human subjects research.',
      'Practice open science: preregister, share data, and publish openly.',
    ],
    relatedSlugs: ['data-analysis-best-practices', 'writing-papers-published'],
  },

  'cross-institutional-collaboration': {
    title: 'Cross-Institutional Collaboration',
    description: 'Managing multi-institution projects, IP considerations, and data sharing agreements.',
    icon: 'Globe',
    tag: null,
    tagColor: null,
    readTime: '13 min read',
    difficulty: 'Advanced',
    lastUpdated: 'December 2025',
    sections: [
      {
        id: 'planning',
        title: 'Planning Multi-Institution Projects',
        paragraphs: [
          'Cross-institutional projects offer access to diverse expertise, resources, and datasets — but they require more coordination than single-institution work. Start with a collaboration agreement covering goals, timelines, resource contributions, and communication protocols.',
          'Create a shared Research Lab on ResearchPro as the central hub. This gives all team members — regardless of institution — access to the same chat, tasks, files, and library without dealing with institutional email or VPN barriers.',
        ],
      },
      {
        id: 'ip-data',
        title: 'IP & Data Sharing Agreements',
        paragraphs: [
          'Intellectual property (IP) ownership can be contentious in multi-institution projects. Negotiate IP terms before the project begins, ideally through your institutions\' technology transfer offices. Common models include shared ownership, lead-institution ownership, or contributor-based ownership.',
          'Data sharing agreements (DSAs) specify what data can be shared, with whom, for what purpose, and under what security requirements. Ensure DSAs are signed before any data transfers. For international collaborations, consider GDPR, HIPAA, or other regulatory frameworks.',
        ],
      },
      {
        id: 'communication',
        title: 'Communication Across Time Zones',
        paragraphs: [
          'With team members across multiple time zones, synchronous meetings become challenging. Establish a core overlap window (even if only 1–2 hours) for critical discussions. Use asynchronous communication (lab chat, task comments) for everything else.',
          'Record important meetings and post summaries in the lab chat for members who could not attend. Use the shared calendar to track each member\'s availability and schedule meetings that rotate to share the inconvenience of off-hours calls.',
        ],
      },
    ],
    keyTakeaways: [
      'Start with a formal collaboration agreement before the project begins.',
      'Negotiate IP ownership and sign data sharing agreements early.',
      'Use a Research Lab as a centralized hub accessible to all institutions.',
      'Rely on asynchronous communication and rotate meeting times.',
    ],
    relatedSlugs: ['high-performance-research-teams', 'research-ethics-integrity'],
  },

  'maximizing-research-impact': {
    title: 'Maximizing Research Impact',
    description: 'Beyond h-index: professional profiles, social media, preprints, and open access.',
    icon: 'TrendingUp',
    tag: null,
    tagColor: null,
    readTime: '9 min read',
    difficulty: 'Intermediate',
    lastUpdated: 'January 2026',
    sections: [
      {
        id: 'metrics',
        title: 'Understanding Impact Metrics',
        paragraphs: [
          'The h-index and citation counts are the most common metrics, but they are slow to accumulate and favor certain fields. Alternative metrics (altmetrics) track social media mentions, policy citations, media coverage, and downloads for a broader picture of impact.',
          'Use multiple metrics together. A paper with few citations but widespread media coverage may have significant public impact. A paper with high citations but no altmetrics has strong academic impact but limited public reach.',
        ],
      },
      {
        id: 'online-presence',
        title: 'Building Your Online Presence',
        paragraphs: [
          'Maintain active profiles on ResearchPro, Google Scholar, ORCID, and ResearchGate. Use a consistent name across all platforms. Your ResearchPro profile showcases your publications, lab memberships, community contributions, and professional background.',
          'Write a compelling bio that highlights your expertise and research interests. Include links to your most impactful publications and current projects. A strong profile attracts collaborators, invitations to review, and speaking opportunities.',
        ],
      },
      {
        id: 'preprints',
        title: 'Preprints & Open Access',
        paragraphs: [
          'Posting preprints (on arXiv, bioRxiv, SSRN) makes your work immediately available while going through peer review. This accelerates dissemination, establishes priority, and allows early feedback from the community.',
          'Many funders now require open-access publication. Choose journals with open-access options or post the accepted manuscript in an institutional repository. Share your papers freely in ResearchPro communities to increase readership.',
        ],
      },
      {
        id: 'social-media',
        title: 'Social Media for Researchers',
        paragraphs: [
          'Share your publications on X/Twitter, LinkedIn, and Bluesky with a plain-language summary of your findings. Tag collaborators and relevant organizations. Thread formats work well for explaining complex research to broader audiences.',
          'Engage with other researchers\' work by commenting and sharing. Build a reputation as a thoughtful contributor, not just a self-promoter. Use ResearchPro\'s Research Feed to share updates with the academic community specifically.',
        ],
      },
    ],
    keyTakeaways: [
      'Use multiple impact metrics, not just h-index and citations.',
      'Maintain consistent, complete profiles across academic platforms.',
      'Post preprints to accelerate dissemination and get early feedback.',
      'Share research on social media with plain-language summaries.',
    ],
    relatedSlugs: ['writing-papers-published', 'ai-tools-researchers'],
  },

  'ai-tools-researchers': {
    title: 'AI Tools for Researchers',
    description: 'Responsible use of AI in research — from literature analysis to drafting with disclosure guidelines.',
    icon: 'Sparkles',
    tag: null,
    tagColor: null,
    readTime: '12 min read',
    difficulty: 'Intermediate',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'literature',
        title: 'AI for Literature Analysis',
        paragraphs: [
          'AI tools can accelerate literature reviews by summarizing papers, extracting key findings, and identifying patterns across large bodies of work. ResearchPro\'s semantic search is itself an AI tool — it understands research questions in natural language and finds conceptually relevant papers.',
          'Tools like Elicit, Semantic Scholar, and Connected Papers complement ResearchPro by offering different analysis perspectives. Use AI summaries as a starting point, but always read the original papers for accuracy.',
        ],
      },
      {
        id: 'writing',
        title: 'AI-Assisted Writing & Editing',
        paragraphs: [
          'Large language models can help with drafting, rephrasing, grammar correction, and formatting. They are most useful for mechanical tasks — generating initial draft structures, converting bullet points into prose, and checking for consistency.',
          'Never submit AI-generated text without thorough review and revision. AI models can produce fluent but inaccurate content, including fabricated citations. Treat AI output as a rough draft that requires expert verification.',
        ],
      },
      {
        id: 'ethics',
        title: 'Ethical Considerations & Disclosure',
        paragraphs: [
          'Most journals now require disclosure of AI tool usage. Follow each journal\'s specific policy — some prohibit AI-written text entirely, others allow it with disclosure. At minimum, acknowledge which tools you used and for what purpose.',
          'Never use AI to fabricate data, generate fake references, or misrepresent the origin of ideas. Academic integrity applies to AI-assisted work the same as any other tool.',
        ],
      },
      {
        id: 'tools',
        title: 'Recommended Tools & Workflows',
        paragraphs: [
          'For literature: ResearchPro semantic search, Elicit, Connected Papers, Research Rabbit. For writing: Grammarly (grammar), Writefull (academic style), Overleaf with AI plugins (LaTeX). For analysis: GitHub Copilot (code), ChatGPT/Claude (brainstorming, outlining).',
          'Build a workflow that uses AI for specific, well-defined tasks: literature mapping → outline generation → draft assistance → grammar checking. Keep human judgment at every decision point.',
        ],
      },
    ],
    keyTakeaways: [
      'Use AI for literature analysis and mechanical writing tasks.',
      'Always verify AI output — models can generate plausible but incorrect text.',
      'Disclose AI usage following your target journal\'s policy.',
      'Keep human judgment at every decision point in your workflow.',
    ],
    relatedSlugs: ['mastering-academic-search', 'complete-literature-reviews'],
  },

  'phd-survival-guide': {
    title: 'PhD Survival Guide',
    description: 'Navigating your doctoral journey — advisor relationships, time management, and well-being.',
    icon: 'GraduationCap',
    tag: null,
    tagColor: null,
    readTime: '16 min read',
    difficulty: 'Beginner',
    lastUpdated: 'January 2026',
    sections: [
      {
        id: 'advisor',
        title: 'Advisor Relationships & Expectations',
        paragraphs: [
          'The advisor-student relationship is the most important factor in PhD success. Have an explicit conversation early about expectations: meeting frequency, communication preferences, feedback turnaround time, and authorship norms.',
          'If the relationship isn\'t working, address it early. Talk to your advisor directly first, then your department\'s graduate director if needed. Changing advisors is normal and sometimes necessary — better to switch early than to struggle for years.',
        ],
      },
      {
        id: 'time-management',
        title: 'Time Management & Productivity',
        paragraphs: [
          'A PhD is a marathon, not a sprint. Set realistic weekly goals rather than vague long-term plans. Block dedicated time for deep work (writing, analysis) and protect it from meetings and emails.',
          'Use your Research Lab task board to track your personal milestones: courses completed, qualifying exams, paper drafts, experiments. Breaking the PhD into concrete tasks makes the multi-year journey less overwhelming.',
        ],
      },
      {
        id: 'wellbeing',
        title: 'Mental Health & Work-Life Balance',
        paragraphs: [
          'PhD students experience higher rates of anxiety and depression than the general population. This is a systemic issue, not a personal failing. Set boundaries: define working hours, take weekends off regularly, and maintain hobbies and relationships outside academia.',
          'Use your institution\'s counseling services — they are confidential and often free. Build a peer support network with other PhD students. When you feel stuck, remember: almost every successful researcher went through the same struggles.',
        ],
      },
      {
        id: 'network',
        title: 'Building Your Professional Network',
        paragraphs: [
          'Start building your network from year one, not just when you are about to graduate. Attend conferences, present at workshops, and participate in ResearchPro communities in your field. These connections lead to collaborations, job opportunities, and lasting professional relationships.',
          'Reach out to researchers whose work you admire — most are happy to discuss their research with engaged PhD students. A brief, specific message ("I found your paper on X fascinating, especially the part about Y") is far more effective than a generic introduction.',
        ],
      },
    ],
    keyTakeaways: [
      'Set explicit expectations with your advisor early in the PhD.',
      'Break the multi-year journey into concrete, trackable milestones.',
      'Set boundaries and maintain interests outside academia.',
      'Build your professional network from year one — don\'t wait.',
    ],
    relatedSlugs: ['high-performance-research-teams', 'maximizing-research-impact'],
  },
};

// ─── Combined lookup ────────────────────────────────────────────────────────

export const ALL_GUIDES = {
  ...DOC_PLATFORM_GUIDES,
  ...DOC_DEVELOPER_GUIDES,
  ...RESEARCH_GUIDES,
};

export default ALL_GUIDES;
